import React, { useEffect, useState, useCallback, useRef } from "react";
import styles from "./styles.module.scss";
import theme from "../../Styles/theme.scss";
import ToggleOnOff from "../ToggleOnOff";
import {
  getDevelcoSensorLEDStatus,
  postDevelcoSensorLEDStatus,
} from "@intelligentlilli/api-layer";

interface LEDSensorToggleProps {
  server: string;
  devicestring: string;
  sensorIds: string[];
}

const LEDSensorToggle = ({
  server,
  devicestring,
  sensorIds,
}: LEDSensorToggleProps) => {
  const [isEnabled, setIsEnabled] = useState<boolean>(false);
  const isTogglingRef = useRef<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const sensorIdsKey = sensorIds.join(",");

  // Fetch current LED status from all motion sensors on mount
  // Toggle is only ON if every sensor reports ON
  useEffect(() => {
    if (sensorIds.length === 0) return;

    let cancelled = false;

    const promises = sensorIds.map((sensorId) =>
      getDevelcoSensorLEDStatus(server, devicestring, sensorId, "web")
    );

    Promise.allSettled(promises).then((results) => {
      if (cancelled) return;

      const allOn = results.every(
        (result) =>
          result.status === "fulfilled" &&
          result.value.ok &&
          result.value.data &&
          "data" in result.value.data &&
          result.value.data.data === true
      );

      setIsEnabled(allOn);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [server, devicestring, sensorIdsKey]);

  const toggleLEDs = useCallback(
    (newStatus: boolean) => {
      if (isTogglingRef.current) return;
      isTogglingRef.current = true;
      setError(null);

      const promises = sensorIds.map((sensorId) =>
        postDevelcoSensorLEDStatus(
          server,
          devicestring,
          sensorId,
          { status: newStatus },
          "web"
        )
      );

      Promise.allSettled(promises).then((results) => {
        const allSucceeded = results.every(
          (result) =>
            result.status === "fulfilled" && result.value.ok
        );

        if (allSucceeded) {
          setIsEnabled(newStatus);
        } else {
          setError("Try again");
        }

        isTogglingRef.current = false;
      });
    },
    [server, devicestring, sensorIds]
  );

  if (sensorIds.length === 0) return null;

  return (
    <div className={styles.container}>
      <p className={styles.title}>Motion and door sensor LEDs</p>
      <div className={styles.bottom_row}>
        <p className={styles.description}>
          Toggle all the motion and door sensor LEDs off or on. Be mindful that with the LEDs off, it will
          be harder to tell if the motion sensors are working.
        </p>
        <div className={styles.toggle_wrapper}>
          <ToggleOnOff
            isEnabled={isEnabled}
            onToggleOn={() => toggleLEDs(true)}
            onToggleOff={() => toggleLEDs(false)}
            backgroundColor={theme.primary3}
          />
        </div>
      </div>
      <p className={styles.error}>{error}</p>
    </div>
  );
};

export default LEDSensorToggle;
